import { deleteCartMutation } from '@/api/carts/cartMutations';
import { Button } from '@/components/common/Button';
import { ConfirmDeleteDialog } from '@/components/common/ConfirmDeleteDialog';
import { TableCell, TableRow } from '@/components/common/Table';
import type { Cart } from '@/domain/Cart';
import { CartState } from '@/domain/Cart';
import { useBreakpoint } from '@/lib/contexts/BreakpointContext';
import { routes } from '@/routes/routes';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CreditCardIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useState } from 'react';

interface CartRowProps {
  cart: Cart;
}

export const CartRow = ({ cart }: CartRowProps) => {
  const { isDesktop } = useBreakpoint();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const deleteCart = useMutation(deleteCartMutation(cart.id));

  const handleDeleteConfirm = () => {
    deleteCart.mutate();
    setDeleteDialogOpen(false);
  };

  if (isDesktop) {
    return (
      <>
        <TableRow>
          <TableCell>{cart.name}</TableCell>

          <TableCell>{cart.state ?? 'Waiting'}</TableCell>

          <TableCell>
            <Link
              to={routes.dashboard.myCarts.cart.items}
              params={{ cartId: cart.id }}
              className="text-primary relative"
            >
              Continue
              {!!cart.created_by_doctor && cart.state === CartState.WAITING && (
                <div className="text-primary-foreground bg-destructive absolute -top-1 -right-4 flex size-4 items-center justify-center rounded-full text-center text-xs">
                  !
                </div>
              )}
            </Link>
          </TableCell>

          <TableCell>
            <button
              onClick={() => setDeleteDialogOpen(true)}
              disabled={deleteCart.isPending}
              className="hover:text-destructive/80 disabled:opacity-50"
            >
              <XIcon className="text-destructive size-3" />
            </button>
          </TableCell>
        </TableRow>
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title="Delete cart"
          description="Are you sure you want to delete this cart?"
          onConfirm={handleDeleteConfirm}
          isPending={deleteCart.isPending}
        />
      </>
    );
  }

  return (
    <>
      <TableRow>
        <TableCell colSpan={4} className="block px-0">
          <div className="border-border flex flex-col gap-3">
            <p className="text-base font-medium">{cart.name}</p>

            <div className="flex gap-3">
              <Button asChild size="sm" className="relative max-w-40 flex-1">
                <Link
                  to={routes.dashboard.myCarts.cart.items}
                  params={{ cartId: cart.id }}
                >
                  <CreditCardIcon className="size-4" />
                  Continue
                  {!!cart.created_by_doctor &&
                    cart.state === CartState.WAITING && (
                      <div className="text-primary-foreground bg-destructive absolute top-0 right-0 flex size-5 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full">
                        !
                      </div>
                    )}
                </Link>
              </Button>

              <Button
                asChild
                variant="accent"
                size="sm"
                className="text-primary-foreground max-w-30 flex-1"
              >
                <p>{cart.state ?? 'Waiting'}</p>
              </Button>

              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleteCart.isPending}
              >
                <Trash2Icon className="size-4" />
              </Button>
            </div>
          </div>
        </TableCell>
      </TableRow>
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete cart"
        description="Are you sure you want to delete this cart?"
        onConfirm={handleDeleteConfirm}
        isPending={deleteCart.isPending}
      />
    </>
  );
};
